"use client"

import type { Omit } from "@wagmi/core/internal"
import { type Config, useChainId, useConfig } from "wagmi"

import { Evaluate } from "viem"
import type { GetAccountOwnerErrorType } from "../actions/getAccountOwner"
import {
  type GetAccountOwnerData,
  type GetAccountOwnerOptions,
  type GetAccountOwnerQueryFnData,
  type GetAccountOwnerQueryKey,
  getAccountOwnerQueryOptions,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
  UseQueryReturnType,
} from "../types"
import { useQuery } from "../utils"

export type UseAccountOwnerParameters<
  config extends Config = Config,
  selectData = GetAccountOwnerData,
> = Evaluate<
  GetAccountOwnerOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetAccountOwnerQueryFnData,
                GetAccountOwnerErrorType,
                selectData,
                GetAccountOwnerQueryKey
              >,
              "gcTime" | "staleTime"
            >
          >
        | undefined
      select?: (data: GetAccountOwnerQueryFnData) => selectData
    }
>

export type UseAccountOwnerReturnType<selectData = GetAccountOwnerData,> =
  UseQueryReturnType<selectData, GetAccountOwnerErrorType>

export function useAccountOwner<
  config extends ExtendedConfig,
  selectData = GetAccountOwnerData,
>(
  parameters: UseAccountOwnerParameters<config, selectData> = {},
): UseAccountOwnerReturnType<selectData> {
  const { query = {}, address, account, scopeKey } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getAccountOwnerQueryOptions(config, {
    address,
    account,
    chainId: parameters.chainId ?? chainId,
    scopeKey,
  })
  const enabled = Boolean(address && account && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    queryKey,
    enabled,
    refetchOnMount: false,
    retry: false,
  })
}
